import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const NAV_ITEMS: readonly { label: string; href: string; isRoute?: boolean }[] = [
  { label: 'Home', href: '/', isRoute: true },
  { label: 'About Us', href: '/about', isRoute: true },
  { label: 'Service', href: '/services', isRoute: true },
  { label: 'Contact Us', href: '/contact', isRoute: true },
];

const LOGO_SRC = '/images/logo/Ancient Engineering logo.png';
const LOGO_WHITE_SRC = `${process.env.PUBLIC_URL || ''}/images/logo/${encodeURIComponent('logo white.png')}`;

export default function Header(): React.ReactElement {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    // Prevent body scroll when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={
        `fixed inset-x-0 top-0 z-40 overflow-visible transition-colors duration-300 ` +
        (isScrolled
          ? 'bg-white text-slate-900 shadow-sm backdrop-blur'
          : 'bg-transparent text-white')
      }
    >
      <div
        className={`mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-12 ${
          isScrolled
            ? 'pt-4 pb-4 sm:pt-8 sm:pb-8'
            : 'py-4 sm:py-8'
        }`}
      >
        <Link
          to="/"
          className="flex items-center gap-4 no-underline"
          onClick={handleNavClick}
        >
          <span className={`relative block w-40 overflow-visible sm:h-16 sm:w-56 ${
            !isScrolled ? 'h-12' : 'h-[60px] sm:h-16'
          }`}>
            <img
              src={!isScrolled ? LOGO_WHITE_SRC : LOGO_SRC}
              alt="Ancient Engineering logo"
              className={`absolute left-0 w-auto object-contain ${
                !isScrolled ? 'h-28 sm:h-36' : 'h-[140px] sm:h-52'
              }`}
              style={{
                top: '50%',
                transform: `translateY(calc(-50% + ${!isScrolled ? '10px' : '5px'}))`,
              }}
            />
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav
          aria-label="Primary navigation"
          className={
            'hidden items-center gap-x-6 text-base font-medium sm:flex ' +
            (isScrolled ? 'text-slate-700' : 'text-white/90')
          }
        >
          {NAV_ITEMS.map(({ label, href, isRoute }) => {
            const className =
              'group relative whitespace-nowrap px-2 py-1 transition-colors duration-200 ' +
              (isScrolled ? 'hover:text-sky-600' : 'hover:text-white');
            const underlineClassName =
              'absolute inset-x-1 bottom-0 h-0.5 origin-left scale-x-0 rounded-full transition-transform duration-200 group-hover:scale-x-100 ' +
              (isScrolled ? 'bg-sky-500' : 'bg-white');

            if (isRoute) {
              return (
                <Link key={label} to={href} className={className}>
                  {label}
                  <span aria-hidden className={underlineClassName} />
                </Link>
              );
            }

            return (
              <a key={label} href={href} className={className}>
                {label}
                <span aria-hidden className={underlineClassName} />
              </a>
            );
          })}
        </nav>

        {/* Mobile Menu Button */}
        <button
          type="button"
          aria-label="Toggle mobile menu"
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={
            'flex items-center justify-center rounded-md p-2 transition-colors sm:hidden ' +
            (isScrolled
              ? 'text-slate-700 hover:bg-slate-100'
              : 'text-white hover:bg-white/10')
          }
        >
          {isMobileMenuOpen ? (
            <CloseIcon fontSize="large" />
          ) : (
            <MenuIcon fontSize="large" />
          )}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={
          'fixed inset-0 z-30 h-screen w-full bg-white shadow-xl transition-transform duration-300 ease-in-out sm:hidden ' +
          (isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full')
        }
      >
        {/* Close Button Inside Menu */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-6">
          <h2 className="text-xl font-semibold text-slate-900">Menu</h2>
          <button
            type="button"
            aria-label="Close mobile menu"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center justify-center rounded-md p-2 text-slate-700 transition-colors hover:bg-slate-100"
          >
            <CloseIcon fontSize="large" />
          </button>
        </div>

        <nav
          aria-label="Mobile navigation"
          className="flex flex-col gap-2 overflow-y-auto px-6 py-8"
          style={{ maxHeight: 'calc(100vh - 80px)' }}
        >
          {NAV_ITEMS.map(({ label, href, isRoute }) => {
            const className =
              'group relative rounded-lg px-6 py-5 text-lg font-semibold text-slate-900 transition-colors hover:bg-sky-50 hover:text-sky-600 border border-transparent hover:border-sky-200';
            const underlineClassName =
              'absolute inset-x-6 bottom-3 h-0.5 origin-left scale-x-0 rounded-full bg-sky-500 transition-transform duration-200 group-hover:scale-x-100';

            if (isRoute) {
              return (
                <Link
                  key={label}
                  to={href}
                  onClick={handleNavClick}
                  className={className}
                >
                  {label}
                  <span aria-hidden className={underlineClassName} />
                </Link>
              );
            }

            return (
              <a
                key={label}
                href={href}
                onClick={handleNavClick}
                className={className}
              >
                {label}
                <span aria-hidden className={underlineClassName} />
              </a>
            );
          })}
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 sm:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
}
